import type { MaybeRefOrGetter } from 'vue'
import type { ComposableOptions, SortedField } from '@/types'
import type { AssetType } from '@/features/assets/constants'
import type { SharedPaginationOptions } from '@/shared/types'

type AssetPaginationOptions = ComposableOptions<SharedPaginationOptions & { owner?: number }>

export namespace queryKeys {
  export namespace yields {
    export function all(): readonly ['asset-yields']
    export function all() {
      return ['asset-yields'] as const
    }

    export function details(): readonly ['asset-yields', 'details']
    export function details(assetCode: MaybeRefOrGetter<string>): readonly ['asset-yields', 'details', MaybeRefOrGetter<string>]
    export function details(assetCode?: MaybeRefOrGetter<string>) {
      return assetCode
        ? [...queryKeys.yields.all(), 'details', assetCode] as const
        : [...queryKeys.yields.all(), 'details'] as const
    }

    export function activityLog(): readonly ['asset-yields', 'activity']
    export function activityLog(assetCode: MaybeRefOrGetter<string>): readonly ['asset-yields', 'activity', MaybeRefOrGetter<string>]
    export function activityLog(assetCode?: MaybeRefOrGetter<string>) {
      return assetCode
        ? [...queryKeys.yields.all(), 'activity', assetCode] as const
        : [...queryKeys.yields.all(), 'activity'] as const
    }
  }

  export namespace assets {
    export function all(): readonly ['assets']
    export function all() {
      return ['assets'] as const
    }

    export function settings(): readonly ['assets', 'settings']
    export function settings<T extends MaybeRefOrGetter<string | undefined>>(assetCode: T): readonly ['assets', 'settings', T]
    export function settings(assetCode?: MaybeRefOrGetter<string | undefined>) {
      return assetCode
        ? [...queryKeys.assets.all(), 'settings', assetCode] as const
        : [...queryKeys.assets.all(), 'settings'] as const
    }

    export function details(): readonly ['assets', 'details']
    export function details<T extends MaybeRefOrGetter<string | undefined>>(assetCode: T): readonly ['assets', 'details', T]
    export function details(assetCode?: MaybeRefOrGetter<string | undefined>) {
      return assetCode
        ? [...queryKeys.assets.all(), 'details', assetCode] as const
        : [...queryKeys.assets.all(), 'details'] as const
    }

    export function paginated(): readonly ['assets', 'paginated']
    export function paginated(options: AssetPaginationOptions): readonly ['assets', 'paginated', AssetPaginationOptions]
    export function paginated(options?: AssetPaginationOptions) {
      return options
        ? [...queryKeys.assets.all(), 'paginated', options] as const
        : [...queryKeys.assets.all(), 'paginated'] as const
    }

    export function creationEligibilityLookup(): readonly ['assets', 'eligibility-lookup']
    export function creationEligibilityLookup<T extends { unifiiProjectCode: MaybeRefOrGetter<number|undefined>, assetType: MaybeRefOrGetter<AssetType>, wheelingProjectCode: MaybeRefOrGetter<string|undefined> }>(data: T): readonly ['assets', 'eligibility-lookup', T]
    export function creationEligibilityLookup(data?: { unifiiProjectCode: MaybeRefOrGetter<number|undefined>, assetType: MaybeRefOrGetter<AssetType>, wheelingProjectCode: MaybeRefOrGetter<string|undefined> }) {
      return data
        ? [...queryKeys.assets.all(), 'eligibility-lookup', data] as const
        : [...queryKeys.assets.all(), 'eligibility-lookup'] as const
    }
  }
}
